import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { DataGridPro, gridClasses, GridColDef } from '@mui/x-data-grid-pro'
import { getMuiDataGridLocaleText } from '../../i18n/localeUtils'
import { Tooltip, Typography, useMediaQuery } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { useTheme } from '@mui/material/styles'

import { normalizedWarrantName } from '@finance-lab/utils/shared'
import InfoBlock from '../components/InfoBlock'
import { formatDateYYYYMMDD, formatMoney } from '../../packages/utils/numberFormatters'
import IssueTypeLabels, { HasWarrantLabel, KVLabel } from '../../components/entities/IssueTypeLabels'
import { TransactionType } from '@finance-lab/utils/types'
import { InvestorDetails } from '../../api/InvestorApi'
import { TFunction } from 'i18next'
import {
    CustomDataGridToolbar,
    CustomDataGridToolbarProps,
} from '../../components/custom-grid-components/CustomDataGridToolbar'
import { hasSourceOfType } from '../../components/utils/calculateIssueStatus'
import { FullHeightBox } from '../../components/UIComponents'

const isPutOptionFlag = (value: boolean | 0 | 1 | null | undefined) => value === true || value === 1

interface HistoricTableRow {
    cancelled: boolean
    commitments: Array<{
        amountInCash: number | null
        commitmentId: string
        date: string | null
        feeLevel: number | null
        isSeller: 0 | 1 | null
        lowestFeeLevel: number | null
        /** Separates TÅ med/utan rätter at fee level 0 */
        withoutRights: number | null
        putOption: boolean | 0 | 1 | null
    }>
    currency: string | null
    date: string | null
    financialAdvisors: Array<{
        name: string
    }> | null
    id: string
    issue: InvestorDetails['participations'][number]['issue'] | null
    instrumentName: string | null
    sector: string | null
    type: TransactionType
    warrant: InvestorDetails['participations'][number]['warrant'] | null
    convertible: InvestorDetails['participations'][number]['convertible'] | null
}

const disableSelectedSellOutlineStyle = {
    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
        outline: 'none',
    },
    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
        outline: 'none',
    },
}

const commitmentTypeText = (type: HistoricTableRow['type'], commitment: HistoricTableRow['commitments'][number]) => {
    if (commitment.isSeller) {
        return 'Säljare'
    }
    const feeLevel = commitment.lowestFeeLevel
    if (feeLevel === 0) {
        return commitment.withoutRights === 1 ? 'TÅ utan rätter' : 'Teckningsåtagande'
    } else if (feeLevel === null) {
        return type === 'Convertible' ? 'Långivare' : 'Deltagare'
    } else if (feeLevel > 0) {
        return isPutOptionFlag(commitment.putOption) ? 'Säljoption' : 'Garantiåtagande'
    }

    return '-'
}

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga historiska emissioner hittades</Typography>
        </Box>
    )
}

const detailsPath = ({
    issue,
    warrant,
    convertible,
}: {
    issue: { id: string } | null
    warrant: { id: string } | null
    convertible: { id: string } | null
}) =>
    issue
        ? `/issues/${issue.id}`
        : warrant
          ? `/warrants/${warrant.id}`
          : convertible
            ? `/convertibles/${convertible.id}`
            : ''

const issuesHistoryCols = (onlyFields: string[], locale: string, t: TFunction, isSmallScreen: boolean) => {
    const cols: GridColDef<HistoricTableRow>[] = [
        {
            field: 'date',
            headerName: 'Datum',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 100,
            maxWidth: 120,
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1)
                const date2 = new Date(v2)
                return date1.getTime() - date2.getTime()
            },
            renderCell: (params) => {
                return <FullHeightBox>{formatDateYYYYMMDD(params.row.date)}</FullHeightBox>
            },
        },
        {
            field: 'instrumentName',
            headerName: 'Emission/TO',
            minWidth: isSmallScreen ? undefined : 200,
            maxWidth: 500,
            flex: 1,
            renderCell: ({ row }) => {
                const nameContent = `${row.instrumentName || '?'}${row.cancelled ? ' (avbruten)' : ''}`
                return (
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        sx={{
                            alignItems: { xs: 'start', md: 'center' },
                            justifyContent: 'center',
                            paddingY: { xs: 2, md: 0 },
                        }}
                    >
                        {row.issue ? <IssueTypeLabels issue={row.issue} isSingleLabel /> : null}
                        {row.warrant ? <HasWarrantLabel /> : null}
                        {row.convertible ? <KVLabel /> : null}
                        <Tooltip title={`Visa detaljer om ${row.issue ? 'emissionen' : 'teckningsoptionen'}`}>
                            <Link to={detailsPath(row)}>{row.cancelled ? <i>{nameContent}</i> : nameContent}</Link>
                        </Tooltip>
                    </Stack>
                )
            },
        },
        {
            field: 'sector',
            headerName: 'Bransch',
            minWidth: 100,
            maxWidth: 400,
            flex: 1,
            renderCell: (params) => <span>{params.row.sector}</span>,
        },
        {
            field: 'financialAdvisors',
            headerName: 'Fin. rådgivare',
            headerAlign: 'left',
            align: 'left',
            minWidth: 120,
            flex: 1,
            sortable: false,
            valueGetter: (financialAdvisors: HistoricTableRow['financialAdvisors']) => {
                if (financialAdvisors === null || financialAdvisors.length === 0) return null

                const num = financialAdvisors.length
                const suffix = num > 1 ? ` +${num - 1}` : ''
                return `${financialAdvisors[0].name} ${suffix}`
            },
        },
        {
            field: 'type',
            headerName: 'Åtagande',
            minWidth: 160,
            maxWidth: 200,
            flex: 1,
            sortComparator: (v1: string, v2: string) => v1.localeCompare(v2),
            renderCell: ({ row }) => (
                <Stack
                    spacing={4}
                    sx={{
                        justifyContent: 'center',
                    }}
                >
                    {row.commitments.map((commitment) => (
                        <Typography
                            key={commitment.commitmentId}
                            sx={{
                                textAlign: 'left',
                            }}
                        >
                            {commitmentTypeText(row.type, commitment)}
                        </Typography>
                    ))}
                </Stack>
            ),
        },
        {
            field: 'commitments',
            headerName: 'Belopp',
            flex: 1,
            maxWidth: 140,
            valueGetter: (value) => parseFloat(value[0].amountInCash ?? 0),
            renderCell: (params) => {
                return (
                    <Stack
                        spacing={{ xs: 2, md: 4 }}
                        sx={[
                            {
                                justifyContent: 'center',
                            },
                            isSmallScreen ? { paddingY: 1 } : null,
                        ]}
                    >
                        {params.row.commitments.map((commitment) => (
                            <Stack key={commitment.commitmentId}>
                                <Typography>
                                    {isSmallScreen ? `${commitmentTypeText(params.row.type, commitment)}: ` : null}
                                </Typography>
                                <Typography>
                                    {commitment.amountInCash
                                        ? formatMoney(commitment.amountInCash, {
                                              locale,
                                              decimals: 0,
                                              currencySymbol: params.row.currency,
                                          })
                                        : '*'}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                )
            },
        },
    ]
    if (t) {
        cols.forEach((col) => {
            if (!col.headerName && col.headerNameI18n) {
                col.headerName = t(col.headerNameI18n)
            }
        })
    }
    return onlyFields ? cols.filter((col) => onlyFields.includes(col.field)) : cols
}

const normalizeWithoutRights = (w: number | null | undefined) => (w === null || Number(w) === 0 ? 0 : 1)

const sortHistoricCommitments = (commitments: HistoricTableRow['commitments']): HistoricTableRow['commitments'] =>
    [...commitments].sort((a, b) => {
        const fd = (a.feeLevel || 0) - (b.feeLevel || 0)
        if (fd !== 0) return fd
        return normalizeWithoutRights(a.withoutRights) - normalizeWithoutRights(b.withoutRights)
    })

const getTableRows = (participations: InvestorDetails['participations']) =>
    participations.reduce((historicIssues, curr) => {
        const { issue, commitment, warrant, convertible } = curr

        const date = issue?.subEndDate || issue?.publicationDate || warrant?.subEndDate || convertible?.publicationDate

        const existingHistoricIssueIndex = historicIssues.findIndex(
            (historicIssue) => historicIssue.id === issue?.id || historicIssue.id === warrant?.id,
        )

        const withoutRightsNorm = commitment.withoutRights === null ? null : Number(commitment.withoutRights)

        const commitmentObject: HistoricTableRow['commitments'][number] = {
            amountInCash: commitment.amountInCash !== null ? parseFloat(commitment.amountInCash) : null,
            commitmentId: commitment.id,
            date: date || null,
            feeLevel: commitment.feeLevel,
            isSeller: commitment.isSeller,
            lowestFeeLevel: commitment.feeLevel !== null && commitment.feeLevel >= 0 ? commitment.feeLevel : null,
            withoutRights: withoutRightsNorm,
            putOption: commitment.putOption ?? null,
        }

        if (existingHistoricIssueIndex >= 0) {
            const existingHistoricIssue = historicIssues[existingHistoricIssueIndex]

            if (commitment.feeLevel !== null && commitment.feeLevel > 0) {
                const guarantFeeLevel = commitment.feeLevel
                const existingGuarantIndex = existingHistoricIssue.commitments.findIndex(
                    (c) => c.feeLevel !== null && c.feeLevel > 0,
                )

                if (existingGuarantIndex >= 0) {
                    const existingGuarantObject = existingHistoricIssue.commitments[existingGuarantIndex]

                    historicIssues[existingHistoricIssueIndex].commitments[existingGuarantIndex] = {
                        ...existingGuarantObject,
                        lowestFeeLevel:
                            existingGuarantObject.lowestFeeLevel !== null &&
                            existingGuarantObject.lowestFeeLevel > guarantFeeLevel
                                ? commitmentObject.feeLevel
                                : existingGuarantObject.lowestFeeLevel,
                        amountInCash: (existingGuarantObject.amountInCash || 0) + (commitmentObject.amountInCash || 0),
                        putOption:
                            isPutOptionFlag(existingGuarantObject.putOption) ||
                            isPutOptionFlag(commitmentObject.putOption),
                    }

                    return historicIssues
                }
            }

            historicIssues[existingHistoricIssueIndex].commitments = sortHistoricCommitments([
                ...existingHistoricIssue.commitments,
                commitmentObject,
            ])
        } else {
            historicIssues.push({
                cancelled: issue ? hasSourceOfType(issue, 'Cancellation') : !!warrant?.cancelled,
                commitments: [commitmentObject],
                currency: issue?.currency || warrant?.currency || convertible?.currency || null,
                date: date || null,
                financialAdvisors:
                    issue?.financialAdvisors || warrant?.financialAdvisors || convertible?.financialAdvisors || [],
                instrumentName:
                    (issue
                        ? issue.instrumentName || issue.shareId
                        : warrant
                          ? normalizedWarrantName(warrant)
                          : convertible
                            ? convertible.convertibleName
                            : null) || null,
                id: issue?.id || warrant?.id || convertible?.id || '',
                issue,
                sector: issue?.sector1 || warrant?.sector1 || convertible?.sector1 || null,
                type: issue ? issue.type : warrant ? 'Warrant' : 'Convertible',
                warrant,
                convertible,
            })
        }

        return historicIssues
    }, [] as Array<HistoricTableRow>)

function InvestorHistory({ investor }: { investor: InvestorDetails }) {
    const [issueGridCurrentPage, setIssueGridCurrentPage] = useState(0)
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    useEffect(() => {
        setIssueGridCurrentPage(0)
    }, [investor.id])

    if (!investor?.participations) return null

    const slots = {
        slots: {
            noResultsOverlay: CustomNoResultsOverlay,
            noRowsOverlay: CustomNoResultsOverlay,
            toolbar: CustomDataGridToolbar,
        },
    }

    const amountNotDefined = investor.participations.some(({ commitment }) => commitment.amountInCash === null)
    const tableRows = getTableRows(investor.participations || [])

    const columnFields = ['date', 'instrumentName', 'commitments']

    if (!isSmallScreen) {
        columnFields.push(...['type', 'sector', 'financialAdvisors'])
    }

    return (
        <InfoBlock title='Historik'>
            <DataGridPro
                sx={{
                    ...disableSelectedSellOutlineStyle,
                    width: '100%',
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                    '& .MuiDataGrid-cell': {
                        display: 'flex',
                    },
                }}
                sortingOrder={['asc', 'desc']}
                localeText={getMuiDataGridLocaleText(locale)}
                pagination
                initialState={{
                    sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
                }}
                pageSizeOptions={[10]}
                disableColumnMenu
                disableRowSelectionOnClick
                rows={tableRows}
                getRowHeight={({ model }) => {
                    if (isSmallScreen) {
                        return 'auto'
                    }
                    if (model.commitments.length > 1) {
                        return 52 + model.commitments.length * 20
                    }

                    return 52
                }}
                columns={issuesHistoryCols(columnFields, locale, t, isSmallScreen)}
                density='standard'
                {...slots}
                slotProps={{
                    toolbar: {
                        showColumnCustomization: false,
                    } as CustomDataGridToolbarProps,
                }}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                paginationModel={{ pageSize: 10, page: issueGridCurrentPage }}
                onPaginationModelChange={({ page }) => {
                    setIssueGridCurrentPage(page)
                }}
                showToolbar
            />
            {amountNotDefined ? (
                <Box
                    sx={{
                        pt: 1,
                    }}
                >
                    <Typography variant='caption'>
                        *<i>Fullständig information angående belopp per deltagare har ej kommunicerats av bolaget.</i>
                    </Typography>
                </Box>
            ) : null}
        </InfoBlock>
    )
}

export default InvestorHistory
